import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { opportunityService, customerService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, Search, Loader2 } from "lucide-react";
import { formatCurrency } from "@/lib/currency";

export const STAGES = ["Qualification", "Needs Analysis", "Proposal", "Negotiation", "Closed Won", "Closed Lost"];
export const STAGE_COLOR = { Qualification: "slate", "Needs Analysis": "blue", Proposal: "amber", Negotiation: "teal", "Closed Won": "green", "Closed Lost": "red" };
export const fmtMoney = (v) => formatCurrency(v);

const EMPTY = { name: "", customerId: "", stage: "Qualification", amount: "", expectedCloseDate: "", description: "", tenantId: "" };

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

export default function OpportunitiesPage() {
  const { hasPermission, user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [customers, setCustomers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await opportunityService.list({ q: q || undefined, stage: stageFilter || undefined, size: 100 }); setRows(d.items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q, stageFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { customerService.list({ size: 200 }).then((d) => setCustomers(d.items || [])).catch(() => {}); }, []);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r, amount: r.amount ?? "", expectedCloseDate: r.expectedCloseDate || "" }); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    if (isSuper && !editing && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = { ...form, amount: form.amount === "" ? 0 : Number(form.amount), expectedCloseDate: form.expectedCloseDate || null };
      if (editing) { delete payload.tenantId; await opportunityService.update(editing.id, payload); toast.success("Opportunity updated"); }
      else { await opportunityService.create(payload); toast.success("Opportunity created"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete opportunity "${r.name}"?`)) return;
    try { await opportunityService.remove(r.id); toast.success("Opportunity deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canCreate = hasPermission("OPPORTUNITY.CREATE");
  const canEdit = hasPermission("OPPORTUNITY.EDIT");
  const canDelete = hasPermission("OPPORTUNITY.DELETE");

  const columns = [
    { key: "opportunityCode", header: "Code", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.opportunityCode}</span> },
    { key: "name", header: "Name", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "customerName", header: "Customer", render: (r) => r.customerName || "—" },
    { key: "stage", header: "Stage", render: (r) => <Badge color={STAGE_COLOR[r.stage] || "slate"}>{r.stage}</Badge> },
    { key: "amount", header: "Amount", render: (r) => fmtMoney(r.amount) },
    { key: "probability", header: "Prob", render: (r) => `${r.probability ?? 0}%` },
    {
      key: "_a", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => navigate(`/admin/opportunities/${r.id}`)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`opp-view-${r.id}`}><Eye className="h-4 w-4" /></button>
          {canEdit && <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`opp-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>}
          {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`opp-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="opportunities-page">
      <PageHeader title="Opportunities" subtitle="Track deals through your sales pipeline"
        actions={canCreate && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="opp-create-btn">
            <Plus className="h-4 w-4" /> New Opportunity
          </button>
        )}
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 w-64">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, customer…" className="bg-transparent text-sm outline-none w-full text-foreground" data-testid="opp-search" />
        </div>
        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="opp-stage-filter">
          <option value="">All stages</option>
          {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <DataTable columns={columns} rows={rows} loading={loading} testid="opportunities-table" empty="No opportunities yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="opp-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit" : "New"} Opportunity</h3>
            <div className="space-y-3.5">
              {isSuper && !editing && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="opp-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ciq-input" data-testid="opp-name" /></Field>
              <Field label="Customer">
                <select value={form.customerId || ""} onChange={(e) => setForm({ ...form, customerId: e.target.value })} className="ciq-input" data-testid="opp-customer">
                  <option value="">—</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.customerName}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Stage"><select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="ciq-input" data-testid="opp-stage">{STAGES.map((s) => <option key={s}>{s}</option>)}</select></Field>
                <Field label="Amount (INR)"><input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="ciq-input" data-testid="opp-amount" /></Field>
              </div>
              <Field label="Expected Close Date"><input type="date" value={form.expectedCloseDate || ""} onChange={(e) => setForm({ ...form, expectedCloseDate: e.target.value })} className="ciq-input" data-testid="opp-close-date" /></Field>
              <Field label="Description"><textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ciq-input" rows={2} /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="opp-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
